import {
  clamp,
  euclideanModulo,
  lerp,
  maxDotNumber,
  parseNumber
} from "@zyzgroup/core-common";
import { BufferAttribute, Matrix3, Vector3 } from "./math";

export const COLOR_NAMES: Record<string, number> = {
  1: 0x0ac2d2, // rgb(10,194,210)
  2: 0x7bb7fa, // rgb(123,183,250)
  3: 0x60d7a9, // rgb(96,215,169)
  4: 0xfdc162, // rgb(253,193,98)
  5: 0xfd6a62, // rgb(253,106,98)
  6: 0xf68dbb, // rgb(246,141,187)
  7: 0x26ccff,
  8: 0xa25afd,
  9: 0xff5e7e,
  10: 0x88ff5a,
  11: 0xfcff42,
  12: 0xffa62d,
  13: 0xff36ff,
  14: 0xdc4916,
  15: 0x6871b1,
  16: 0xd1b935,
  17: 0x1bbc9b,
  18: 0xff7f50,
  19: 0x4bbfc3,
  20: 0xff9900,
  d: 0xdddddd, // rgb(221,221,221)
  aliceblue: 0xf0f8ff, // rgb(240,248,255)
  antiquewhite: 0xfaebd7, // rgb(250,235,215)
  aqua: 0x00ffff, // rgb(0,255,255)
  aquamarine: 0x7fffd4, // rgb(127,255,212)
  azure: 0xf0ffff,
  beige: 0xf5f5dc,
  bisque: 0xffe4c4,
  black: 0x000000, // rgb(0,0,0)
  blanchedalmond: 0xffebcd,
  blue: 0x0000ff, // rgb(0,0,255)
  blueviolet: 0x8a2be2,
  brown: 0xa52a2a,
  burlywood: 0xdeb887,
  cadetblue: 0x5f9ea0,
  chartreuse: 0x7fff00,
  chocolate: 0xd2691e,
  coral: 0xff7f50,
  cornflowerblue: 0x6495ed,
  cornsilk: 0xfff8dc,
  crimson: 0xdc143c,
  cyan: 0x00ffff,
  darkblue: 0x00008b,
  darkcyan: 0x008b8b,
  darkgoldenrod: 0xb8860b,
  darkgray: 0xa9a9a9,
  darkgreen: 0x006400,
  darkgrey: 0xa9a9a9,
  darkkhaki: 0xbdb76b,
  darkmagenta: 0x8b008b,
  darkolivegreen: 0x556b2f,
  darkorange: 0xff8c00,
  darkorchid: 0x9932cc,
  darkred: 0x8b0000,
  darksalmon: 0xe9967a,
  darkseagreen: 0x8fbc8f,
  darkslateblue: 0x483d8b,
  darkslategray: 0x2f4f4f,
  darkslategrey: 0x2f4f4f,
  darkturquoise: 0x00ced1,
  darkviolet: 0x9400d3,
  deeppink: 0xff1493,
  deepskyblue: 0x00bfff,
  dimgray: 0x696969,
  dimgrey: 0x696969,
  dodgerblue: 0x1e90ff,
  firebrick: 0xb22222,
  floralwhite: 0xfffaf0,
  forestgreen: 0x228b22,
  fuchsia: 0xff00ff,
  gainsboro: 0xdcdcdc,
  ghostwhite: 0xf8f8ff,
  gold: 0xffd700,
  goldenrod: 0xdaa520,
  gray: 0x808080,
  green: 0x008000,
  greenyellow: 0xadff2f,
  grey: 0x808080,
  honeydew: 0xf0fff0,
  hotpink: 0xff69b4,
  indianred: 0xcd5c5c,
  indigo: 0x4b0082,
  ivory: 0xfffff0,
  khaki: 0xf0e68c,
  lavender: 0xe6e6fa,
  lavenderblush: 0xfff0f5,
  lawngreen: 0x7cfc00,
  lemonchiffon: 0xfffacd,
  lightblue: 0xadd8e6,
  lightcoral: 0xf08080,
  lightcyan: 0xe0ffff,
  lightgoldenrodyellow: 0xfafad2,
  lightgray: 0xd3d3d3,
  lightgreen: 0x90ee90,
  lightgrey: 0xd3d3d3,
  lightpink: 0xffb6c1,
  lightsalmon: 0xffa07a,
  lightseagreen: 0x20b2aa,
  lightskyblue: 0x87cefa,
  lightslategray: 0x778899,
  lightslategrey: 0x778899,
  lightsteelblue: 0xb0c4de,
  lightyellow: 0xffffe0,
  lime: 0x00ff00,
  limegreen: 0x32cd32,
  linen: 0xfaf0e6,
  magenta: 0xff00ff,
  maroon: 0x800000,
  mediumaquamarine: 0x66cdaa,
  mediumblue: 0x0000cd,
  mediumorchid: 0xba55d3,
  mediumpurple: 0x9370db,
  mediumseagreen: 0x3cb371,
  mediumslateblue: 0x7b68ee,
  mediumspringgreen: 0x00fa9a,
  mediumturquoise: 0x48d1cc,
  mediumvioletred: 0xc71585,
  midnightblue: 0x191970,
  mintcream: 0xf5fffa,
  mistyrose: 0xffe4e1,
  moccasin: 0xffe4b5,
  navajowhite: 0xffdead,
  navy: 0x000080,
  oldlace: 0xfdf5e6,
  olive: 0x808000,
  olivedrab: 0x6b8e23,
  orange: 0xffa500,
  orangered: 0xff4500,
  orchid: 0xda70d6,
  palegoldenrod: 0xeee8aa,
  palegreen: 0x98fb98,
  paleturquoise: 0xafeeee,
  palevioletred: 0xdb7093,
  papayawhip: 0xffefd5,
  peachpuff: 0xffdab9,
  peru: 0xcd853f,
  pink: 0xffc0cb,
  plum: 0xdda0dd,
  powderblue: 0xb0e0e6,
  purple: 0x800080,
  rebeccapurple: 0x663399,
  red: 0xff0000, // rgb(255,0,0)
  rosybrown: 0xbc8f8f,
  royalblue: 0x4169e1,
  saddlebrown: 0x8b4513,
  salmon: 0xfa8072,
  sandybrown: 0xf4a460,
  seagreen: 0x2e8b57,
  seashell: 0xfff5ee,
  sienna: 0xa0522d,
  silver: 0xc0c0c0,
  skyblue: 0x87ceeb,
  slateblue: 0x6a5acd,
  slategray: 0x708090,
  slategrey: 0x708090,
  snow: 0xfffafa,
  springgreen: 0x00ff7f,
  steelblue: 0x4682b4,
  tan: 0xd2b48c,
  teal: 0x008080,
  thistle: 0xd8bfd8,
  tomato: 0xff6347,
  turquoise: 0x40e0d0,
  violet: 0xee82ee,
  wheat: 0xf5deb3,
  white: 0xffffff, // rgb(255,255,255)
  whitesmoke: 0xf5f5f5,
  yellow: 0xffff00, // rgb(255,255,0)
  yellowgreen: 0x9acd32 // rgb(154,205,50)
};

// 透明度合成
function alphaBlend(
  // 前景色
  foreground: {
    r: 0; // 0 ~ 255
    g: 0; // 0 ~ 255
    b: 0; // 0 ~ 255
    a: 1; // 0 ~ 1
  },
  // 背景色
  background: {
    r: 0; // 0 ~ 255
    g: 0; // 0 ~ 255
    b: 0; // 0 ~ 255
  }
) {
  // 计算合成的颜色
  return {
    r: Math.round(
      foreground.a * foreground.r + (1 - foreground.a) * background.r
    ),
    g: Math.round(
      foreground.a * foreground.g + (1 - foreground.a) * background.g
    ),
    b: Math.round(
      foreground.a * foreground.b + (1 - foreground.a) * background.b
    )
  };
}

export class Color {
  private _color = {
    r: 0, // 0 ~ 255
    g: 0, // 0 ~ 255
    b: 0, // 0 ~ 255
    a: 1 // 0 ~ 1
  };

  constructor(color?: any, forceAlpha?: number) {
    if (color) {
      this.setColor(color);
    }
    this.setAlpha(forceAlpha);
  }

  get color() {
    return this._color;
  }
  set color(color: any) {
    const ErrMsg = `Color: Unknown color(${color}) type(${typeof color})`;
    if (typeof color === "number") {
      // hex
      this.setHEX(color);
    } else if (typeof color === "string") {
      // string
      let m;
      if ((m = /^(\w+)\(([^)]*)\)/.exec(color))) {
        // rgb / hsl
        const components = m[2];
        let matches;
        switch (m[1]) {
          case "rgb":
          case "rgba":
            if (
              (matches =
                /^\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*(\d*\.?\d+)\s*)?$/.exec(
                  components
                ))
            ) {
              // rgb(255,0,0) rgba(255,0,0,0.5)
              this.r = parseInt(matches[1], 10);
              this.g = parseInt(matches[2], 10);
              this.b = parseInt(matches[3], 10);
              this.a = matches[4] ? parseFloat(matches[4]) : 1;
            } else if (
              (matches =
                /^\s*(\d+)%\s*,\s*(\d+)%\s*,\s*(\d+)%\s*(?:,\s*(\d*\.?\d+)\s*)?$/.exec(
                  components
                ))
            ) {
              // rgb(100%,0%,0%) rgba(100%,0%,0%,0.5)
              this.r = (clamp(parseFloat(matches[1]), 0, 100) * 255) / 100;
              this.g = (clamp(parseFloat(matches[2]), 0, 100) * 255) / 100;
              this.b = (clamp(parseFloat(matches[3]), 0, 100) * 255) / 100;
              this.a = matches[4] ? parseFloat(matches[4]) : 1;
            } else {
              // throw new Error(ErrMsg);
              console.warn(ErrMsg);
            }
            break;
          case "hsl":
          case "hsla":
            if (
              (matches =
                /^\s*(\d*\.?\d+)\s*,\s*(\d*\.?\d+)%\s*,\s*(\d*\.?\d+)%\s*(?:,\s*(\d*\.?\d+)\s*)?$/.exec(
                  components
                ))
            ) {
              // hsl(120,50%,50%) hsla(120,60.00%,1.00%,0.50)
              this.setHSL(
                parseFloat(matches[1]) / 360,
                parseFloat(matches[2]) / 100,
                parseFloat(matches[3]) / 100,
                matches[4] ? parseFloat(matches[4]) : 1
              );
            } else {
              // throw new Error(ErrMsg);
              console.warn(ErrMsg);
            }
            break;
          case "hsv":
          case "hsva":
          case "hsb":
          case "hsba":
            if (
              (matches =
                /^\s*(\d*\.?\d+)\s*,\s*(\d*\.?\d+)%\s*,\s*(\d*\.?\d+)%\s*(?:,\s*(\d*\.?\d+)\s*)?$/.exec(
                  components
                ))
            ) {
              // hsv(120,50%,50%) hsva(120,60.00%,1.00%,0.50)
              // hsb(120,50%,50%) hsba(120,60.00%,1.00%,0.50)
              this.setHSV(
                parseFloat(matches[1]) / 360,
                parseFloat(matches[2]) / 100,
                parseFloat(matches[3]) / 100,
                matches[4] ? parseFloat(matches[4]) : 1
              );
            } else {
              // throw new Error(ErrMsg);
              console.warn(ErrMsg);
            }
            break;
          default:
            // throw new Error(ErrMsg);
            console.warn(ErrMsg);
        }
      } else if ((m = /#([A-Fa-f0-9]+)/.exec(color))) {
        // #RGB #RRGGBB #RRGGBBAA
        const hex = m[1];
        const size = hex.length;
        if (size === 3) {
          // #RGB
          this.setRGB(
            parseInt(hex.charAt(0), 16),
            parseInt(hex.charAt(1), 16),
            parseInt(hex.charAt(2), 16),
            1
          );
        } else if (size === 6 || size === 8) {
          // #RRGGBB #RRGGBBAA
          this.setHEX(parseInt(hex, 16));
        } else {
          // throw new Error(ErrMsg);
          console.warn(ErrMsg);
        }
      } else if (color && color.length > 0) {
        try {
          this.setColorName(color);
        } catch (e) {
          // throw new Error(ErrMsg);
          console.warn(ErrMsg);
        }
      } else {
        // throw new Error(ErrMsg);
        console.warn(ErrMsg);
      }
    } else if (color instanceof Color) {
      this.setRGB(color.r, color.g, color.b, color.a);
    } else if (
      Object.prototype.hasOwnProperty.call(color, "r") &&
      Object.prototype.hasOwnProperty.call(color, "g") &&
      Object.prototype.hasOwnProperty.call(color, "b")
    ) {
      this.setRGB(
        color.r,
        color.g,
        color.b,
        Object.prototype.hasOwnProperty.call(color, "a") ? color.a : 1
      );
    } else if (
      Object.prototype.hasOwnProperty.call(color, "h") &&
      Object.prototype.hasOwnProperty.call(color, "s") &&
      Object.prototype.hasOwnProperty.call(color, "l")
    ) {
      this.setHSL(
        color.h,
        color.s,
        color.l,
        Object.prototype.hasOwnProperty.call(color, "a") ? color.a : 1
      );
    } else if (
      Object.prototype.hasOwnProperty.call(color, "h") &&
      Object.prototype.hasOwnProperty.call(color, "s") &&
      Object.prototype.hasOwnProperty.call(color, "v")
    ) {
      this.setHSV(
        color.h,
        color.s,
        color.v,
        Object.prototype.hasOwnProperty.call(color, "a") ? color.a : 1
      );
    } else if (
      Object.prototype.hasOwnProperty.call(color, "h") &&
      Object.prototype.hasOwnProperty.call(color, "s") &&
      Object.prototype.hasOwnProperty.call(color, "b")
    ) {
      this.setHSV(
        color.h,
        color.s,
        color.b,
        Object.prototype.hasOwnProperty.call(color, "a") ? color.a : 1
      );
    } else {
      // throw new Error(ErrMsg);
      console.warn(ErrMsg);
    }
  }
  setColor(color: any) {
    this.color = color;
    return this;
  }
  set(color: any) {
    return this.setColor(color);
  }

  get r() {
    return this._color.r;
  }
  set r(v: any) {
    this._color.r = clamp(Math.round(parseNumber(v, this.r)), 0, 255);
  }

  get g() {
    return this._color.g;
  }
  set g(v: any) {
    this._color.g = clamp(Math.round(parseNumber(v, this.g)), 0, 255);
  }

  get b() {
    return this._color.b;
  }
  set b(v: any) {
    this._color.b = clamp(Math.round(parseNumber(v, this.b)), 0, 255);
  }

  get a() {
    return this._color.a;
  }
  set a(a: any) {
    this._color.a = clamp(parseNumber(a, this.a), 0, 1);
  }
  setAlpha(a: any) {
    this.a = a;
    return this;
  }

  setFromVector3(v: Vector3) {
    this.r = v.x;
    this.g = v.y;
    this.b = v.z;
    return this;
  }

  applyMatrix3(m: Matrix3) {
    const r = this.r;
    const g = this.g;
    const b = this.b;
    const e = m.value;
    this.r = e[0] * r + e[3] * g + e[6] * b;
    this.g = e[1] * r + e[4] * g + e[7] * b;
    this.b = e[2] * r + e[5] * g + e[8] * b;
    return this;
  }

  fromBufferAttribute(attribute: BufferAttribute, index: number) {
    this.r = attribute.getX(index);
    this.g = attribute.getY(index);
    this.b = attribute.getZ(index);
    return this;
  }

  fromArray(array: number[], offset = 0) {
    this.r = array[offset];
    this.g = array[offset + 1];
    this.b = array[offset + 2];
    this.a = array[offset + 3];
    return this;
  }

  toArray(array: number[] = [], offset = 0) {
    array[offset] = this.r;
    array[offset + 1] = this.g;
    array[offset + 2] = this.b;
    array[offset + 3] = this.a;
    return array;
  }

  toRGBString(): string {
    if (this.a === 1) {
      return `rgb(${this.r},${this.g},${this.b})`;
    } else {
      return `rgba(${this.r},${this.g},${this.b},${maxDotNumber(this.a, 6)})`;
    }
  }

  toHexString(): string {
    if (this.a === 1) {
      return ("000000" + this.hex.toString(16)).slice(-6);
    } else {
      return ("00000000" + this.hex.toString(16)).slice(-8);
    }
  }

  toString(): string {
    if (this.a === 1) {
      return "#" + this.toHexString();
    } else {
      return this.toRGBString();
    }
  }

  toColorSpaceString(colorSpace = SRGBColorSpace): string {
    ColorManagement.fromWorkingColorSpace(__color.copy(this), colorSpace);
    const r = __color.r;
    const g = __color.g;
    const b = __color.b;
    if (colorSpace !== SRGBColorSpace) {
      // Requires CSS Color Module Level 4 (https://www.w3.org/TR/css-color-4/).
      return `color(${colorSpace} ${r.toFixed(3)} ${g.toFixed(3)} ${b.toFixed(
        3
      )})`;
    } else {
      return this.toRGBString();
    }
  }

  *[Symbol.iterator]() {
    yield this.r;
    yield this.g;
    yield this.b;
    yield this.a;
  }

  clone(): Color {
    return new Color(this.color);
  }

  copy(c: Color) {
    this.setColor(c.color);
    return this;
  }

  copySRGBToLinear(color: Color) {
    this.r = SRGBToLinear(color.r);
    this.g = SRGBToLinear(color.g);
    this.b = SRGBToLinear(color.b);
    // this.a = SRGBToLinear(color.a);
    return this;
  }

  copyLinearToSRGB(color: Color) {
    this.r = LinearToSRGB(color.r);
    this.g = LinearToSRGB(color.g);
    this.b = LinearToSRGB(color.b);
    // this.a = LinearToSRGB(color.a);
    return this;
  }

  convertSRGBToLinear() {
    this.copySRGBToLinear(this);
    return this;
  }

  convertLinearToSRGB() {
    this.copyLinearToSRGB(this);
    return this;
  }

  equals(c: Color) {
    return c.r === this.r && c.g === this.g && c.b === this.b && c.a === this.a;
  }

  add(color: Color) {
    this.r = this.r + color.r;
    this.g = this.g + color.g;
    this.b = this.b + color.b;
    return this;
  }

  addColors(color1: Color, color2: Color) {
    this.r = color1.r + color2.r;
    this.g = color1.g + color2.g;
    this.b = color1.b + color2.b;
    return this;
  }

  addScalar(s: number) {
    this.r = this.r + s;
    this.g = this.g + s;
    this.b = this.b + s;
    return this;
  }

  setScalar(scalar: number) {
    this.r = scalar;
    this.g = scalar;
    this.b = scalar;
    return this;
  }

  sub(color: Color) {
    this.r = this.r - color.r;
    this.g = this.g - color.g;
    this.b = this.b - color.b;
    return this;
  }

  multiply(color: Color) {
    this.r = this.r * color.r;
    this.g = this.g * color.g;
    this.b = this.b * color.b;
    return this;
  }

  multiplyScalar(s: number) {
    this.r = this.r * s;
    this.g = this.g * s;
    this.b = this.b * s;
    return this;
  }

  opposite(
    bw = true, // whether to invert to black or white
    blackColor = "#000000",
    whiteColor = "#ffffff"
  ) {
    if (bw) {
      // https://stackoverflow.com/a/3943023/112731
      return this.setColor(
        this.r * 0.299 + this.g * 0.587 + this.b * 0.114 > 186
          ? blackColor
          : whiteColor
      );
    } else {
      const { r, g, b } = this.color;
      this.r = 255 - r;
      this.g = 255 - g;
      this.b = 255 - b;
      return this;
    }
  }

  closet(blackColor = "#000000", whiteColor = "#ffffff") {
    return this.opposite(true, blackColor, whiteColor).opposite(
      true,
      blackColor,
      whiteColor
    );
  }

  // t: 0 ~ 1
  lighten(t: number) {
    t = clamp(t, 0, 1);
    const { r, g, b, a } = this.color;
    if (a == 0) {
      this.a = t;
    } else {
      this.r = r + (255 - r) * t;
      this.g = g + (255 - g) * t;
      this.b = b + (255 - b) * t;
    }
    return this;
  }

  // t: 0 ~ 1
  darken(t: number) {
    t = clamp(t, 0, 1);
    const { r, g, b, a } = this.color;
    if (a == 0) {
      this.a = t;
    } else {
      this.r = r * (1 - t);
      this.g = g * (1 - t);
      this.b = b * (1 - t);
    }
    return this;
  }

  // 渐变色
  // t: 0 ~ 1
  lerp(color: Color, t: number) {
    t = clamp(t, 0, 1);
    const { r, g, b } = this.color;
    this.r = r + (color.r - r) * t;
    this.g = g + (color.g - g) * t;
    this.b = b + (color.b - b) * t;
    return this;
  }

  // 渐变色
  // t: 0 ~ 1
  lerpColors(start: Color, end: Color, t: number) {
    t = clamp(t, 0, 1);
    this.r = start.r + (end.r - start.r) * t;
    this.g = start.g + (end.g - start.g) * t;
    this.b = start.b + (end.b - start.b) * t;
    return this;
  }

  // 渐变色
  // t: 0 ~ 1
  static gradient(start: string, end: string, t: number): string {
    try {
      return __color.lerpColors(new Color(start), new Color(end), t).toString();
    } catch (e) {
      return end;
    }
  }

  static random(): string {
    return __color.setHEX(Math.random() * 0xffffff).toString();
  }

  static nextIndex = 0;
  static next(startIndex = Color.nextIndex): string {
    Color.nextIndex = startIndex;
    const keys = Object.keys(COLOR_NAMES);
    const length = keys.length;
    return __color
      .setHEX(COLOR_NAMES[keys[Color.nextIndex++ % length]])
      .toString();
  }

  setColorName(style: string) {
    const hex = COLOR_NAMES[style.toLowerCase()];
    if (hex !== undefined) {
      this.setHEX(hex);
    } else if (style === "none") {
      this.a = 0;
    } else {
      this.a = 1;
      // throw new Error("Color.setColorName: Unknown color " + style);
      console.warn("Color.setColorName: Unknown color " + style);
    }
    return this;
  }
  static getColors(): string[] {
    return Object.values(COLOR_NAMES).map((v) => {
      return __color.setHEX(v).toString();
    });
  }

  setRGB(r: number, g: number, b: number, a = 1) {
    this.r = r;
    this.g = g;
    this.b = b;
    this.a = a;
    return this;
  }

  get rgb() {
    return {
      r: this.r,
      g: this.g,
      b: this.b,
      a: this.a
    };
  }

  // RRGGBB(AA)
  setHEX(hex: number) {
    hex = Math.floor(hex);
    // 16777216 = Math.pow(2, 24)
    if (hex < 16777216) {
      this.r = (hex >> 16) & 0xff;
      this.g = (hex >> 8) & 0xff;
      this.b = hex & 0xff;
      this.a = 1;
    } else {
      this.r = (hex >> 24) & 0xff;
      this.g = (hex >> 16) & 0xff;
      this.b = (hex >> 8) & 0xff;
      this.a = (hex & 0xff) / 255;
    }
    return this;
  }

  get hex(): number {
    let hex = (this.r << 16) + (this.g << 8) + this.b;
    if (this.a !== 1) {
      // hex += Math.round(this.a * 255) << 24;
      hex = hex << 8;
      hex += Math.round(this.a * 255);
    }
    return hex;
  }

  /// hsl(a) : hue 色相 + saturation 饱和度 + lightness luminosity 亮度
  /// h,s,l ranges are in 0.0 ~ 1.0
  setHSL(_h: number, _s: number, _l: number, a = 1) {
    const h = euclideanModulo(_h, 1);
    const s = clamp(_s, 0, 1);
    const l = clamp(_l, 0, 1);
    if (s === 0) {
      this.r = l * 255;
      this.g = l * 255;
      this.b = l * 255;
    } else {
      const p = l <= 0.5 ? l * (1 + s) : l + s - l * s;
      const q = 2 * l - p;
      this.r = hue2rgb(q, p, h + 1 / 3) * 255;
      this.g = hue2rgb(q, p, h) * 255;
      this.b = hue2rgb(q, p, h - 1 / 3) * 255;
    }
    this.a = a;
    return this;
  }

  getHSL(target?: Color) {
    const r = this.r;
    const g = this.g;
    const b = this.b;
    const max = Math.max(r, g, b) / 255;
    const min = Math.min(r, g, b) / 255;
    let hue, saturation;
    const lightness = (min + max) / 2.0;
    if (min === max) {
      hue = 0;
      saturation = 0;
    } else {
      const delta = max - min;
      saturation =
        lightness <= 0.5 ? delta / (max + min) : delta / (2 - max - min);
      switch (max) {
        case r:
          hue = (g - b) / delta + (g < b ? 6 : 0);
          break;
        case g:
          hue = (b - r) / delta + 2;
          break;
        case b:
        default:
          hue = (r - g) / delta + 4;
          break;
      }
      hue /= 6;
    }
    if (target) {
      target.setHSL(hue, saturation, lightness, this.a);
    }
    return {
      h: hue,
      s: saturation,
      l: lightness,
      a: this.a
    };
  }

  offsetHSL(h: number, s: number, l: number) {
    const hsl = this.getHSL();
    return this.setHSL(hsl.h + h, hsl.s + s, hsl.l + l);
  }

  lerpHSL(color: Color, t: number) {
    const hsla = this.getHSL();
    const hslb = color.getHSL();
    const h = lerp(hsla.h, hslb.h, t);
    const s = lerp(hsla.s, hslb.s, t);
    const l = lerp(hsla.l, hslb.l, t);
    this.setHSL(h, s, l);
    return this;
  }

  /// hsv(a) / hsb(a) : hue 色相 + saturation 饱和度 + value or brightness 明度
  /// h,s,v ranges are in 0.0 ~ 1.0
  setHSV(_h: number, _s: number, _v: number, a = 1) {
    const h = clamp(_h, 0, 1) * 360;
    const s = clamp(_s, 0, 1);
    const v = clamp(_v, 0, 1);
    const hi = Math.floor(h / 60) % 6;
    const f = h / 60 - Math.floor(h / 60);
    const p = v * (1.0 - s);
    const q = v * (1.0 - f * s);
    const t = v * (1.0 - (1.0 - f) * s);
    const c = [
      [v, t, p],
      [q, v, p],
      [p, v, t],
      [p, q, v],
      [t, p, v],
      [v, p, q]
    ][hi];
    this.r = c[0] * 255;
    this.g = c[1] * 255;
    this.b = c[2] * 255;
    this.a = a;
    return this;
  }
  setHSB(_h: number, _s: number, _b: number, a = 1) {
    this.setHSV(_h, _s, _b, a);
  }

  getHSV(target?: Color) {
    const { r, g, b, a } = this.color;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const delta = max - min;
    let h;
    if (r === max) {
      h = (g - b) / delta;
    } else if (g === max) {
      h = 2 + (b - r) / delta;
    } else {
      h = 4 + (r - g) / delta;
    }
    h /= 6;
    if (h < 0) {
      h += 1;
    }
    const _h = delta == 0 ? 0 : h;
    const _s = max === 0 ? 0 : delta / max;
    const _v = max / 255;
    if (target) {
      target.setHSV(_h, _s, _v, a);
    }
    return {
      h: _h,
      s: _s,
      v: _v,
      a: a
    };
  }
  getHSB(target?: Color) {
    const { h, s, v, a } = this.getHSV(target);
    return {
      h,
      s,
      b: v,
      a
    };
  }
}

// 全局可复用的对象
export const __color = new Color();

// Color space string identifiers, matching CSS Color Module Level 4 and WebGPU names where available.
export const NoColorSpace = "";
export const SRGBColorSpace = "srgb";
export const LinearSRGBColorSpace = "srgb-linear";

export const LinearTransfer = "linear";
export const SRGBTransfer = "srgb";

export function SRGBToLinear(c: number): number {
  return c < 0.04045
    ? c * 0.0773993808
    : Math.pow(c * 0.9478672986 + 0.0521327014, 2.4);
}

export function LinearToSRGB(c: number): number {
  return c < 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 0.41666) - 0.055;
}

const LINEAR_REC709_TO_XYZ: Matrix3 = new Matrix3().set(
  0.4123908,
  0.3575843,
  0.1804808,
  0.212639,
  0.7151687,
  0.0721923,
  0.0193308,
  0.1191948,
  0.9505322
);
const XYZ_TO_LINEAR_REC709: Matrix3 = new Matrix3().set(
  3.2409699,
  -1.5373832,
  -0.4986108,
  -0.9692436,
  1.8759675,
  0.0415551,
  0.0556301,
  -0.203977,
  1.0569715
);
const REC709_PRIMARIES = [0.64, 0.33, 0.3, 0.6, 0.15, 0.06];
const REC709_LUMINANCE_COEFFICIENTS = [0.2126, 0.7152, 0.0722];
const D65 = [0.3127, 0.329];

export const ColorManagement = {
  enabled: true,

  workingColorSpace: LinearSRGBColorSpace,

  /**
   * Implementations of supported color spaces.
   *
   * Required:
   *	- primaries: chromaticity coordinates [ rx ry gx gy bx by ]
   *	- whitePoint: reference white [ x y ]
   *	- transfer: transfer function (pre-defined)
   *	- toXYZ: Matrix3 RGB to XYZ transform
   *	- fromXYZ: Matrix3 XYZ to RGB transform
   *	- luminanceCoefficients: RGB luminance coefficients
   *
   * Optional:
   *  - outputColorSpaceConfig: { drawingBufferColorSpace: ColorSpace }
   *  - workingColorSpaceConfig: { unpackColorSpace: ColorSpace }
   *
   * Reference:
   * - https://www.russellcottrell.com/photo/matrixCalculator.htm
   */
  spaces: {} as Record<string, Record<string, any>>,

  convert: function (
    color: Color,
    sourceColorSpace: string,
    targetColorSpace: string
  ): Color {
    if (
      this.enabled === false ||
      sourceColorSpace === targetColorSpace ||
      !sourceColorSpace ||
      !targetColorSpace
    ) {
      return color;
    }

    if (this.spaces[sourceColorSpace].transfer === SRGBTransfer) {
      color.r = SRGBToLinear(color.r);
      color.g = SRGBToLinear(color.g);
      color.b = SRGBToLinear(color.b);
      // color.a = SRGBToLinear(color.a);
    }

    if (
      this.spaces[sourceColorSpace].primaries !==
      this.spaces[targetColorSpace].primaries
    ) {
      color.applyMatrix3(this.spaces[sourceColorSpace].toXYZ);
      color.applyMatrix3(this.spaces[targetColorSpace].fromXYZ);
    }

    if (this.spaces[targetColorSpace].transfer === SRGBTransfer) {
      color.r = LinearToSRGB(color.r);
      color.g = LinearToSRGB(color.g);
      color.b = LinearToSRGB(color.b);
      // color.a = LinearToSRGB(color.a);
    }

    return color;
  },

  fromWorkingColorSpace: function (color: Color, targetColorSpace: string) {
    return this.convert(color, this.workingColorSpace, targetColorSpace);
  },

  toWorkingColorSpace: function (color: Color, sourceColorSpace: string) {
    return this.convert(color, sourceColorSpace, this.workingColorSpace);
  },

  getPrimaries: function (colorSpace: string) {
    return this.spaces[colorSpace].primaries;
  },

  getTransfer: function (colorSpace: string) {
    if (colorSpace === NoColorSpace) {
      return LinearTransfer;
    }
    return this.spaces[colorSpace].transfer;
  },

  getLuminanceCoefficients: function (
    this: any,
    target: Color,
    colorSpace = this.workingColorSpace
  ) {
    return target.fromArray(this.spaces[colorSpace].luminanceCoefficients);
  },

  define: function (colorSpaces: Record<string, Record<string, any>>) {
    Object.assign(this.spaces, colorSpaces);
  },

  // Internal APIs

  _getMatrix: function (
    targetMatrix: Matrix3,
    sourceColorSpace: string,
    targetColorSpace: string
  ) {
    return targetMatrix
      .copy(this.spaces[sourceColorSpace].toXYZ)
      .multiply(this.spaces[targetColorSpace].fromXYZ);
  },

  _getDrawingBufferColorSpace: function (colorSpace: string) {
    return this.spaces[colorSpace].outputColorSpaceConfig
      .drawingBufferColorSpace;
  },

  _getUnpackColorSpace: function (
    this: any,
    colorSpace = this.workingColorSpace
  ) {
    return this.spaces[colorSpace].workingColorSpaceConfig.unpackColorSpace;
  }
};

ColorManagement.define({
  [LinearSRGBColorSpace]: {
    primaries: REC709_PRIMARIES,
    whitePoint: D65,
    transfer: LinearTransfer,
    toXYZ: LINEAR_REC709_TO_XYZ,
    fromXYZ: XYZ_TO_LINEAR_REC709,
    luminanceCoefficients: REC709_LUMINANCE_COEFFICIENTS,
    workingColorSpaceConfig: { unpackColorSpace: SRGBColorSpace },
    outputColorSpaceConfig: { drawingBufferColorSpace: SRGBColorSpace }
  },

  [SRGBColorSpace]: {
    primaries: REC709_PRIMARIES,
    whitePoint: D65,
    transfer: SRGBTransfer,
    toXYZ: LINEAR_REC709_TO_XYZ,
    fromXYZ: XYZ_TO_LINEAR_REC709,
    luminanceCoefficients: REC709_LUMINANCE_COEFFICIENTS,
    outputColorSpaceConfig: { drawingBufferColorSpace: SRGBColorSpace }
  }
});

function hue2rgb(p: number, q: number, t: number) {
  if (t < 0) t += 1;
  if (t > 1) t -= 1;
  if (t < 1 / 6) return p + (q - p) * 6 * t;
  if (t < 1 / 2) return q;
  if (t < 2 / 3) return p + (q - p) * 6 * (2 / 3 - t);
  return p;
}
